import {
  AccountCircle,
  Close,
  InfoOutlined,
  NoAccounts,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Joi from 'joi-browser';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { addUser } from '../../../../redux/user/actions';
import { primary } from '../../../../utils/brandColors';
import theme from '../../../../utils/theme';

import AboutUserRolesModal from '../../../../pages/settings/UserManagementPage/components/AboutUserRolesModal';

export const NewUserModalComponent = ({
  open,
  onNewUser,
  onCancel,
  onClose,
  firstName: passedInFirstName,
  lastName: passedInLastName,
}) => {
  const { mode } = useColorScheme();
  const dispatch = useDispatch();
  const fullscreen = useMediaQuery(theme.breakpoints.down('md'));

  const { isUpdatingBarnUsers } = useSelector((state) => state.barn);

  const [firstName, setFirstName] = useState('');
  const [firstNameValid, setFirstNameValid] = useState(true);
  const [lastName, setLastName] = useState('');
  const [lastNameValid, setLastNameValid] = useState(true);
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(true);
  const [error, setError] = useState('');
  const [role, setRole] = useState('[user]');
  const [showAboutUserRolesModal, setShowAboutUserRolesModal] = useState(false);

  React.useEffect(() => {
    setFirstName(passedInFirstName);
    setLastName(passedInLastName);
    setRole('[user]');
  }, [passedInFirstName, passedInLastName])

  const handleSaveName = () => {
    setError(false);
    if (email && Joi.validate(email, Joi.string().email()).error) {
      setEmailValid(false);
      return;
    } setEmailValid(true);
    if (firstName.length < 2 || firstName.length > 50) {
      setFirstNameValid(false);
      return;
    } setFirstNameValid(true);
    if (firstName.length < 2 || firstName.length > 50) {
      setLastNameValid(false);
      return;
    } setLastNameValid(true);
    dispatch(addUser({
      email: email || null,
      firstName,
      lastName,
      roles: role,
    }, false)).then((data) => {
      onNewUser(data);
    }).catch((responseError) => {
      setError(responseError.data && responseError.data.message
        ? responseError.data.message
        : 'There was an error adding user, please contact support.');
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          m: 0,
          mx: fullscreen
            ? 0
            : 2,
          width: '100%',
          maxWidth: 720,
        },
      }}
      aria-labelledby="modal-add-access-note"
      aria-describedby="modal-add-horse-access-description"
    >
      <DialogTitle
        sx={{
          background: mode === 'light'
            ? primary.main
            : 'var(--template-palette-background-paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h4" component="span" color="#fff">Create New User</Typography>
        <IconButton aria-label="Close" onClick={onClose}><Close sx={{ color: '#fff' }} /></IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ p: 3 }}
      >
        <form>
          <Grid container spacing={1}>
            <Grid size={4}>
              <TextField
                required
                size="small"
                error={!firstNameValid}
                margin="normal"
                variant="outlined"
                id="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                label="First Name"
                helperText={!firstNameValid && 'Please provide a first name'}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                required
                size="small"
                error={!lastNameValid}
                margin="normal"
                variant="outlined"
                id="last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                label="Last Name"
                helperText={!lastNameValid && 'Please provide a last name'}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                size="small"
                error={!emailValid}
                margin="normal"
                variant="outlined"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                label="Email"
                helperText={!emailValid && 'Provide a valid email'}
              />
            </Grid>
            <Grid
              sx={{
                mb: 4,
                display: 'flex',
                alignItems: 'center',
              }}
              size={12}
            >
              {(email && email.length > 0 && emailValid)
                ? (
                  <AccountCircle color="primary" />
                )
                : (
                  <NoAccounts color="secondary" />
                )}
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: 14,
                  ml: 2,
                }}
              >
                {(email && email.length > 0 && emailValid)
                  ? 'Questri will email the user at the provided email address promiting them to create an account.'
                  : 'No account will be created for this user. Provide an email to allow user access and login.'}
              </Typography>
            </Grid>
          </Grid>
          <FormControl>
            <FormLabel id="demo-form-control-label-placement">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Select a Role
                <Tooltip title="About User Roles">
                  <IconButton sx={{ ml: 1 }} onClick={() => setShowAboutUserRolesModal(true)}>
                    <InfoOutlined />
                  </IconButton>
                </Tooltip>
              </Box>
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-form-control-label-placement"
              name="position"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <FormControlLabel
                value="[user]"
                control={<Radio />}
                label="Client"
                labelPlacement="top"
              />
              <FormControlLabel
                value="[user,staff]"
                control={<Radio />}
                label="Staff"
                labelPlacement="top"
              />
              <FormControlLabel
                value="[user,farrier]"
                control={<Radio />}
                label="Farrier"
                labelPlacement="top"
              />
              <FormControlLabel
                value="[user,veterinarian]"
                control={<Radio />}
                label="Veterinarian"
                labelPlacement="top"
              />
            </RadioGroup>
          </FormControl>
        </form>

        <Alert severity="info" sx={{ m: 2 }}>
          <Box>New users will NOT have access to Questri unless you provide an email and explicitly assign Staff level permissions.</Box>
        </Alert>

        <AboutUserRolesModal
          open={showAboutUserRolesModal}
          onClose={() => setShowAboutUserRolesModal(false)}
        />
        <Divider sx={{ mt: 3 }} />
      </DialogContent>
      <DialogActions
        sx={{
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Button
          onClick={() => onCancel()}
          disabled={isUpdatingBarnUsers}
          id="cancel-edit-button"
          variant="outlined"
        >
          Do Not Create User
        </Button>
        {error && <Typography component="div" paddingBottom variant="caption" color="error">{error}</Typography>}
        <Button
          onClick={handleSaveName}
          loading={isUpdatingBarnUsers}
          color="primary"
          variant="contained"
          autoFocus
          id="confirm-save-button"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewUserModalComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  onNewUser: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
};
NewUserModalComponent.defaultProps = {
  firstName: '',
  lastName: '',
};

export default NewUserModalComponent;
